import './index.scss'

import * as React from 'react'
import ReactDropdown, { Option } from 'react-dropdown'
import Filter from '../../../atoms/Filter'
import { distinct, flatten } from '../../../services'
import { ImageBoxData } from '../../../services/types'
import Header from '../Header'
import Listing from './components/Listing'

export interface ExtraPagesMainProps {
  data: ImageBoxData[];
  itemType?: string;
  title: string;
  lang: 'ro' | 'en';
}

export interface ExtraPagesState {
  filter: string;
  extraFilters: string[];
  showActiveOnly: boolean;
  isLoadingState: boolean;
}

export default class ExtraPagesMain extends React.Component<ExtraPagesMainProps, ExtraPagesState> {
  public static defaultProps: Partial<ExtraPagesMainProps> = {
    itemType: 'members active',
  };

  public state: ExtraPagesState = {
    filter: '',
    showActiveOnly: false,
    isLoadingState: false,
    extraFilters: ['current'],
  };

  public yearSort = (a: ImageBoxData, b: ImageBoxData): number => {
    if (isNaN(a.startYear)) {
      a.startYear = 9999;
    }
    if (isNaN(b.startYear)) {
      b.startYear = 9999;
    }

    if (a.startYear > b.startYear) {
      return 1;
    } else if (a.startYear < b.startYear) {
      return -1;
    } else {
      return 0;
    }
  };

  get data(): ImageBoxData[] {
    const { showActiveOnly, extraFilters } = this.state;
    const { data, title } = this.props;

    // Remove diacritics from filter
    const filter = this.state.filter.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const nb = parseInt(filter, 10);
    let result: ImageBoxData[] = [];

    if (!isNaN(nb)) {
      if (nb >= 1900 && nb <= 2019) {
        result = data.filter((entry: any) => entry.startYear <= nb && nb <= entry.endYear);
      }
    } else {
      result = data.filter((entry: any) =>
        entry.name
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .includes(filter),
      );
    }

    if (title === 'Community' || title === 'Comunitate') {
      if (showActiveOnly) {
        result = result.filter((entry) => entry.endYear === 2019);
      }
    }

    // Sort by start year
    result = result.sort(this.yearSort);

    if (extraFilters.length === 0) {
      return result;
    }

    // Extra filter
    return result.filter((entry) => {
      const extraFields = entry.extra.split(',').map((field) => field.trim());
      let isInScope = false;

      extraFields.forEach((field) => {
        if (extraFilters.includes(field)) {
          isInScope = true;
        }
      });

      return isInScope;
    });
  }

  get secondaryText() {
    const { filter } = this.state;
    const { lang, itemType } = this.props;

    const nb = parseInt(filter, 10);

    if (!isNaN(nb) && nb > 1900 && nb < 2019) {
      return lang === 'en' ? `Showing ${itemType} in the year ${nb}` : `Afișăm ${itemType} în anul ${nb}`;
    } else {
      return '';
    }
  }

  get filterOptions(): Option[] {
    const { lang } = this.props

    return [
      {
        label: lang === 'ro'
          ? 'Cadrele didactice din Departamentul de Calculatoare'
          : 'Department of Computers teaching staff',
        value: 'current',
      },
      {
        label: lang === 'ro'
          ? 'Cadrele didactice care au activat de-a lungul celor 50 de ani'
          : 'Teaching staff across the last 50 years',
        value: 'complete',
      },
      {
        label: lang === 'ro'
          ? 'In memoriam'
          : 'In memoriam',
        value: 'tot',
      },
      {
        label: lang === 'ro'
          ? 'Conducători de doctorat'
          : 'Doctoral supervisors',
        value: 'PHD',
      },
      {
        label: lang === 'ro'
          ? 'Conducerea departamentului'
          : 'Department management',
        value: 'CS',
      },
      {
        label: lang === 'ro'
          ? 'Conducerea facultății'
          : 'Faculty management',
        value: 'ACS',
      },
      {
        label: lang === 'ro'
          ? 'Conducerea universității'
          : 'University management',
        value: 'UPB',
      },
      {
        label: lang === 'ro'
          ? 'Foruri științifice (academii)'
          : 'Members of scientific forums (Academicians)',
        value: 'Academy',
      },
    ]
  }

  get extraFilters() {
    const { extraFilters } = this.state;
    const { data, title } = this.props;
    const fields = flatten(data.map((entry) => entry.extra.split(',')))
      .filter((field) => field.trim() !== '')
      .map((field) => field.trim())
      .filter(distinct)
      .sort();

    if (fields.length === 1) {
      return null;
    }

    if (title === 'Community' || title === 'Comunitate') {
      const setFilter = (filter: Option) => {
        const filterObject = this.filterOptions.find((option) => option.value === filter.value)

        if (filterObject === undefined) {
          return
        }

        this.setState(
          { isLoadingState: true },
          () => setTimeout(() => this.setState({ isLoadingState: false, extraFilters: [filterObject.value] }), 500),
        )
      }

      const getFilter = () => {
        if (extraFilters.length === 0) {
          return this.filterOptions[0]
        } else {
          const filterObject = this.filterOptions.find((option) => option.value === extraFilters[0])

          if (filterObject) {
            return filterObject
          } else {
            return this.filterOptions[0]
          }
        }
      }

      return (
        <div className="extraFilters">
          <ReactDropdown
            value={getFilter()}
            options={this.filterOptions}
            onChange={setFilter}
          />
        </div>
      )
    } else {
      return (
        <div className="extraFilters">
          {fields.map((field: string, key: number) => {
            const onClick = () => this.setExtraFilter(field);

            return (
              <h2
                key={key}
                className={`field ${field} ${extraFilters.includes(field) ? 'active' : ''}`}
                onClick={onClick}
              >
                {field}
              </h2>
            );
          })}
        </div>
      );
    }
  }

  get title() {
    const currentFilter = this.state.extraFilters[0]
    const currentOption = this.filterOptions.find((option) => option.value === currentFilter)

    if (!currentOption) {
      return this.props.title
    }

    return currentOption.label
  }

  public render() {
    const { title } = this.props
    const { isLoadingState } = this.state;

    return (
      <main className={`page ${title.toLowerCase()}`}>
        <Header />
        <nav>
          <h1>{this.title}</h1>
          <h2>{this.secondaryText}</h2>
          {this.extraFilters}
          <Filter onChange={this.onFilterChange} />
        </nav>
        {isLoadingState
          ? <h2 className="loading">{this.props.lang === 'en' ? 'Loading...' : 'Se încarcă...'}</h2>
          : <Listing lang={this.props.lang} data={this.data} extraFilters={this.state.extraFilters} />
        }
      </main>
    );
  }

  private cancelLoadingState = () => this.setState({ isLoadingState: false })
  private onFilterChange = (filter: string) => {
    this.setState({ filter, isLoadingState: true })
    setTimeout(this.cancelLoadingState, 500);
  };
  private setActive = () => {
    this.setState({ showActiveOnly: true, isLoadingState: true });
    setTimeout(this.cancelLoadingState, 500);
    this.resetScrollPosition();
  };
  private unsetActive = () => {
    this.setState({ showActiveOnly: false, isLoadingState: true });
    setTimeout(this.cancelLoadingState, 500);
    this.resetScrollPosition();
  };
  private setExtraFilter = (filter: string) => {
    let { extraFilters } = this.state;

    if (extraFilters.includes(filter)) {
      extraFilters = extraFilters.filter((entry) => entry !== filter);
    } else {
      extraFilters.push(filter);
    }

    this.setState({ extraFilters, isLoadingState: true });
    setTimeout(this.cancelLoadingState, 500);
    this.resetScrollPosition();
  };
  private resetScrollPosition = () => {
    const container = document.querySelector('.contentList');
    if (container) {
      container.scrollTop = 0;
    }
  };
}
